if (!customElements.get('nfd-womens-product')) {
  customElements.define('nfd-womens-product', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.controller) return;
        this.controller = new AbortController();
        this.addEventListener('change', event => {
          if (!event.target.matches('input[data-option-position]')) return;
          event.stopPropagation();
          this.selectSize(event.target);
        }, { capture: true, signal: this.controller.signal });
        this.addEventListener('variant:change', () => queueMicrotask(() => {
          this.syncAvailability();
          this.syncChartSelection(true);
        }), { signal: this.controller.signal });
        const tablist = this.querySelector('[data-women-tabs]');
        const tabs = [...tablist.querySelectorAll('[role="tab"]')];
        const fabricTab = tabs.find(tab => tab.getAttribute('aria-controls').endsWith('-fabric'));
        const fitTab = tabs.find(tab => tab.getAttribute('aria-controls').endsWith('-fit'));
        const mobileTabs = window.matchMedia('(max-width: 999px)');
        const card = this.querySelector('.nfd-women-buycard');
        const arrangeTabs = () => {
          tablist.prepend(fabricTab);
          const information = this.querySelector('.nfd-women-information');
          const first = information?.querySelector('.nfd-women-information__lead');
          if (first) information.prepend(first);
          const fitCopy = information?.querySelector('.nfd-women-fit');
          const fitHome = information?.querySelector('.nfd-women-information__intro');
          const fitPanel = this.querySelector('#' + CSS.escape(fitTab.getAttribute('aria-controls')));
          if (fitCopy && fitHome && fitPanel) {
            if (mobileTabs.matches) fitPanel.prepend(fitCopy);
            else fitHome.append(fitCopy);
          }
          tabs.splice(0, tabs.length, ...tablist.querySelectorAll('[role="tab"]'));
        };
        arrangeTabs();
        this.formatPolicyContent();
        mobileTabs.addEventListener('change', arrangeTabs, { signal: this.controller.signal });
        const formatSizeTools = () => {
          const sizeTools = card.querySelector('.nfd-size-tools-inline');
          if (!sizeTools) return;
          const chartLink = sizeTools.querySelector('.nfd-size-chart-toggle');
          if (chartLink && chartLink.textContent !== this.dataset.sizeChartLabel) chartLink.textContent = this.dataset.sizeChartLabel;
          const label = sizeTools.querySelector('[data-rec-trigger-label]');
          const text = label?.textContent.replace(/^\s*⊹\s*/, '').trim();
          if (label && label.textContent !== text) label.textContent = text;
        };
        this.sizeToolsObserver = new MutationObserver(formatSizeTools);
        this.sizeToolsObserver.observe(card, { childList: true, subtree: true, characterData: true });
        formatSizeTools();
        this.activateTab = (tab, focus = false) => {
          tabs.forEach(item => {
            const selected = item === tab;
            item.setAttribute('aria-selected', String(selected));
            item.tabIndex = selected ? 0 : -1;
            this.querySelector('#' + CSS.escape(item.getAttribute('aria-controls'))).hidden = !selected;
          });
          if (focus) tab.focus();
        };
        this.querySelector('[data-women-tabs]').hidden = false;
        this.dataset.tabsReady = '';
        this.activateTab(tabs[0]);
        const intro = this.querySelector('[data-women-intro-text]');
        const introToggle = this.querySelector('[data-women-intro-toggle]');
        if (intro && introToggle) {
          let expanded = false;
          let previousMobile;
          const fullIntro = intro.textContent;
          const normalizeIntro = text => text.replace(/\s+/g, ' ').trim();
          const opening = [...this.querySelectorAll('[data-women-description] p, [data-women-description] div')]
            .find(element => normalizeIntro(element.textContent) === normalizeIntro(fullIntro) && !element.querySelector('p, div'));
          const openingText = opening?.textContent || fullIntro;
          const sentences = typeof Intl.Segmenter === 'function'
            ? [...new Intl.Segmenter(document.documentElement.lang || 'en', { granularity: 'sentence' }).segment(openingText)]
            : [{ segment: openingText, index: 0 }];
          const sentenceCount = Math.min(2, sentences.length);
          const splitAt = sentences[sentenceCount]?.index ?? openingText.length;
          const updateIntro = () => {
            if (previousMobile !== mobileTabs.matches) {
              previousMobile = mobileTabs.matches;
              intro.textContent = mobileTabs.matches && opening ? openingText.slice(0, splitAt).trim() : fullIntro;
            }
            intro.classList.remove('is-clamped');
            const lineHeight = Number.parseFloat(getComputedStyle(intro).lineHeight);
            const collapsible = !mobileTabs.matches && intro.getBoundingClientRect().height > lineHeight * 6 + 1;
            if (!collapsible) expanded = false;
            intro.classList.toggle('is-clamped', collapsible && !expanded);
            introToggle.hidden = !collapsible;
            introToggle.setAttribute('aria-expanded', String(expanded));
            introToggle.setAttribute('aria-label', expanded ? introToggle.dataset.collapseLabel : introToggle.dataset.expandLabel);
            introToggle.querySelector('span').textContent = expanded ? '−' : '→';
            this.dispatchEvent(new CustomEvent('nfd:intro-update'));
          };
          introToggle.addEventListener('click', () => { expanded = !expanded; updateIntro(); }, { signal: this.controller.signal });
          mobileTabs.addEventListener('change', updateIntro, { signal: this.controller.signal });
          let lastWidth;
          this.introResizeObserver = new ResizeObserver(entries => {
            const width = entries[0].contentRect.width;
            if (width !== lastWidth) { lastWidth = width; updateIntro(); }
          });
          this.introResizeObserver.observe(intro.parentElement);
          document.fonts.ready.then(() => { if (this.isConnected) updateIntro(); });
          updateIntro();
        }
        this.addEventListener('click', event => {
          const recommendedNotify = event.target.closest('[data-women-recommended-notify]');
          if (recommendedNotify && !this.hasAttribute('aria-busy')) {
            const input = document.getElementById(recommendedNotify.dataset.womenRecommendedNotify);
            if (input && this.contains(input)) {
              this.pendingRecommendedNotify = input.id;
              if (input.checked) this.syncAvailability();
              else {
                input.checked = true;
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }
          const tab = event.target.closest('[role="tab"]');
          if (tab) this.activateTab(tab);
          const sizeButton = event.target.closest('[data-women-sticky-option]');
          if (sizeButton) {
            const input = document.getElementById(sizeButton.dataset.womenStickyOption);
            const picker = sizeButton.closest('details');
            picker.open = false; picker.querySelector('summary').focus();
            if (input) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
          }
          if (event.target.closest('[data-women-sticky-notify]')) {
            const notify = this.querySelector('[data-nfd-back-in-stock]');
            notify?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            notify?.querySelector('input[type="email"]')?.focus({ preventScroll: true });
          }
        }, { signal: this.controller.signal });
        this.addEventListener('keydown', event => {
          const index = tabs.indexOf(event.target);
          if (index === -1) return;
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.activateTab(tabs[next], true);
        }, { signal: this.controller.signal });
        document.addEventListener('click', event => {
          const picker = this.querySelector('[data-women-sticky-picker]');
          if (picker?.open && !picker.contains(event.target)) picker.open = false;
        }, { signal: this.controller.signal });
        this.addEventListener('keydown', event => {
          const picker = this.querySelector('[data-women-sticky-picker]');
          if (event.key === 'Escape' && picker?.open) {
            picker.open = false; picker.querySelector('summary').focus();
          }
        }, { signal: this.controller.signal });
        const recommendation = this.querySelector('[data-rec-inline]');
        const model = this.querySelector('.nfd-women-buycard .nfd-women-model');
        if (recommendation && model) {
          model.closest('[data-block-allow-rerender]')?.removeAttribute('data-block-allow-rerender');
          model.after(recommendation);
          const formatRecommendation = () => {
            const label = recommendation.querySelector('.nfd-size-rec-result-title');
            const prefix = label?.firstChild;
            if (prefix?.nodeType === Node.TEXT_NODE && prefix.textContent !== this.dataset.recommendedLabel + ' ') prefix.textContent = this.dataset.recommendedLabel + ' ';
            const info = recommendation.querySelector('[data-rec-inline-open]');
            if (info && !info.querySelector('svg')) {
              info.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6.5" stroke="currentColor"/><path d="M8 7v4M8 4.5v.5" stroke="currentColor" stroke-width="1.2"/></svg>';
              info.setAttribute('aria-label', this.dataset.recommendedInfo);
            }
            this.syncRecommendationAvailability();
          };
          this.recommendationObserver = new MutationObserver(formatRecommendation);
          this.recommendationObserver.observe(recommendation, { childList: true, subtree: true, characterData: true });
          formatRecommendation();
        }
        const chart = this.querySelector('.nfd-size-chart-panel');
        const chartDialog = this.querySelector('[data-women-chart-dialog]');
        if (chart) {
          chartDialog.addEventListener('click', event => {
            const button = event.target.closest('[data-women-chart-size]');
            if (!button) return;
            event.preventDefault();
            this.chartHighlightedSize = button.dataset.womenChartSize;
            this.syncChartSelection(true);
          }, { signal: this.controller.signal });
          chartDialog.append(chart);
          const chartTitle = chartDialog.querySelector('.nfd-women-chart-dialog__header p');
          const titleMatch = chartTitle?.textContent.match(/unsanforized|non sanforis[ée]/iu);
          if (titleMatch) {
            const title = chartTitle.textContent;
            const emphasis = document.createElement('strong');
            emphasis.textContent = titleMatch[0];
            chartTitle.replaceChildren(document.createTextNode(title.slice(0, titleMatch.index)), emphasis, document.createTextNode(title.slice(titleMatch.index + titleMatch[0].length)));
          }
          const close = chart.querySelector('.nfd-size-chart-close');
          if (close) chartDialog.querySelector('.nfd-women-chart-dialog__header').append(close);
          const help = chart.querySelector('.nfd-size-chart-help');
          if (help) {
            const heading = help.querySelector('.nfd-size-chart-help-title');
            const guide = help.querySelector('.nfd-size-chart-help-link');
            if (heading) heading.textContent = this.dataset.measureHelp;
            if (guide) guide.textContent = this.dataset.readMeasuring;
            const video = help.querySelector('.nfd-size-chart-video');
            const embed = video?.querySelector('iframe')?.getAttribute('src');
            if (embed) {
              const url = new URL(embed, window.location.origin);
              const id = url.pathname.split('/').pop();
              if (url.protocol === 'https:' && ['www.youtube.com', 'www.youtube-nocookie.com', 'youtube.com'].includes(url.hostname) && /^[A-Za-z0-9_-]{11}$/.test(id)) {
                const link = document.createElement('a');
                link.className = 'nfd-size-chart-help-link';
                link.href = 'https://www.youtube.com/watch?v=' + id;
                link.target = '_blank'; link.rel = 'noopener noreferrer';
                link.textContent = this.dataset.watchMeasuring;
                video.replaceWith(link);
              }
            }
          }
          this.chartObserver = new MutationObserver(() => this.syncChartSelection());
          this.chartObserver.observe(chart, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden'] });
          this.chartResizeObserver = new ResizeObserver(() => this.syncChartSelection(true));
          this.chartResizeObserver.observe(chartDialog);
        }
        this.setupSpecialChart();
        const photoDialog = this.querySelector('[data-women-photo-dialog]');
        document.addEventListener('click', event => {
          const link = event.target.closest('[data-women-editorial-open]');
          if (!link || link.closest('[data-women-editorial-product]')?.dataset.womenEditorialProduct !== this.dataset.productId) return;
          event.preventDefault();
          const thumbnail = link.querySelector('img');
          const image = document.createElement('img');
          image.alt = thumbnail?.alt || '';
          image.src = link.href;
          if (thumbnail?.getAttribute('width')) image.width = Number(thumbnail.getAttribute('width'));
          if (thumbnail?.getAttribute('height')) image.height = Number(thumbnail.getAttribute('height'));
          photoDialog.querySelector('[data-women-photo-stage]').replaceChildren(image);
          this.dialogTrigger = link;
          photoDialog.showModal();
        }, { signal: this.controller.signal });
        const videoDialog = this.querySelector('[data-women-video-dialog]');
        this.addEventListener('click', event => {
          const openChart = event.target.closest('[data-women-chart-open], .nfd-size-chart-toggle');
          const closeChart = event.target.closest('.nfd-size-chart-close');
          const openVideo = event.target.closest('[data-women-video-open]');
          const closeVideo = event.target.closest('[data-women-video-close]');
          const closePhoto = event.target.closest('[data-women-photo-close]');
          if (openChart && chart) {
            event.preventDefault();
            openChart.classList.add('nfd-size-chart-toggle');
            const stickyPicker = openChart.closest('[data-women-sticky-picker]');
            this.dialogTrigger = stickyPicker?.querySelector('summary') || openChart;
            if (stickyPicker) stickyPicker.open = false;
            chart.hidden = true;
            this.chartHighlightedSize = null;
            chartDialog.showModal();
            requestAnimationFrame(() => this.syncChartSelection(true));
          }
          if (closeChart) {
            event.preventDefault(); event.stopPropagation();
            chartDialog.close();
          }
          if (openVideo && videoDialog) {
            this.dialogTrigger = openVideo;
            videoDialog.showModal();
            videoDialog.querySelector('video-media')?.play();
          }
          if (closeVideo) videoDialog.close();
          if (closePhoto) photoDialog.close();
        }, { capture: true, signal: this.controller.signal });
        this.querySelectorAll('.nfd-women-dialog').forEach(dialog => {
          dialog.addEventListener('close', () => {
            dialog.querySelectorAll('video-media').forEach(player => player.pause());
            if (dialog === chartDialog && chart) {
              chart.hidden = true;
              this.querySelectorAll('.nfd-size-chart-toggle').forEach(link => link.setAttribute('aria-expanded', 'false'));
            }
            this.dialogTrigger?.focus({ preventScroll: true });
          }, { signal: this.controller.signal });
          dialog.addEventListener('keydown', event => {
            if (event.key === 'Escape') { event.preventDefault(); dialog.close(); }
          }, { signal: this.controller.signal });
          dialog.addEventListener('click', event => {
            if (event.target !== dialog) return;
            const rect = dialog.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
          }, { signal: this.controller.signal });
        });
        const normalize = text => text.replace(/\s+/g, ' ').trim();
        const fit = normalize(this.querySelector('.nfd-women-fit p')?.textContent || '');
        const fitName = normalize(this.querySelector('.nfd-women-heading__fit')?.textContent || this.querySelector('.nfd-women-heading h1')?.textContent || '');
        this.querySelectorAll('[data-women-description] p, [data-women-description] div').forEach(element => {
          const text = normalize(element.textContent);
          const olderFit = element.tagName === 'P' && fitName && (text.startsWith(`${fitName} is `) || text.startsWith(`The ${fitName} is `));
          if (fit && (text === fit || olderFit)) element.remove();
        });
        this.resizeObserver = new ResizeObserver(entries => {
          this.style.setProperty('--women-card-height', `${Math.ceil(entries[0].target.getBoundingClientRect().height)}px`);
        });
        this.resizeObserver.observe(this.querySelector('.nfd-women-buycard'));
        const editorial = document.querySelector('[data-women-editorial-product="' + this.dataset.productId + '"]');
        if (editorial) {
          const story = editorial.querySelector('[data-women-story]');
          if (story && typeof Intl.Segmenter === 'function') {
            const segmenter = new Intl.Segmenter(document.documentElement.lang || 'en', { granularity: 'sentence' });
            const sentences = [...story.content.querySelectorAll('p')]
              .map(paragraph => paragraph.textContent.trim())
              .filter(text => text && !text.startsWith('*'))
              .flatMap(text => [...segmenter.segment(text)].map(sentence => sentence.segment.trim()));
            editorial.querySelectorAll('.nfd-women-editorial__item').forEach((figure, index) => {
              figure.querySelector('figcaption')?.remove();
              const text = sentences.slice(index * 2, index * 2 + 2).join(' ');
              if (!text) return;
              const caption = document.createElement('figcaption');
              caption.className = 'nfd-women-editorial__caption';
              const paragraph = document.createElement('p');
              paragraph.textContent = text;
              caption.append(paragraph);
              figure.append(caption);
            });
          }
          this.querySelectorAll('[data-women-description] p, [data-women-description] div').forEach(element => {
            if (!element.textContent.trim() && !element.querySelector('img, video, iframe, svg, table, ul, ol, button, a, input')) element.dataset.womenEmptyBlock = '';
          });
          const specifications = editorial.querySelector('.nfd-women-editorial__specifications');
          const storyBoundary = editorial.querySelector('.nfd-women-editorial') || editorial;
          const desktop = window.matchMedia('(min-width: 1000px)');
          if (specifications) specifications.dataset.womenFloatingDetails = '';
          this.syncFloatingDetails = () => {
            const photoBottom = this.querySelector('.nfd-women-hero').getBoundingClientRect().bottom;
            if (!specifications) return;
            const top = Number.parseFloat(getComputedStyle(specifications).top) || 0;
            const bottom = storyBoundary.getBoundingClientRect().bottom;
            const passedPhotos = photoBottom <= top + 48;
            specifications.classList.toggle('is-visible', desktop.matches && passedPhotos && bottom > top + 24);
            specifications.style.transform = desktop.matches ? `translateY(${Math.min(0, bottom - 24 - top - specifications.offsetHeight)}px)` : '';
          };
          const scheduleDetails = () => {
            if (this.detailsFrame) return;
            this.detailsFrame = requestAnimationFrame(() => { this.detailsFrame = null; this.syncFloatingDetails(); });
          };
          window.addEventListener('scroll', scheduleDetails, { passive: true, signal: this.controller.signal });
          window.addEventListener('resize', scheduleDetails, { passive: true, signal: this.controller.signal });
          desktop.addEventListener('change', scheduleDetails, { signal: this.controller.signal });
          this.syncFloatingDetails();
          this.editorialResizeObserver = new ResizeObserver(entries => {
            this.style.setProperty('--women-editorial-height', `${Math.ceil(entries[0].target.getBoundingClientRect().height)}px`);
            this.syncFloatingDetails?.();
          });
          this.editorialResizeObserver.observe(editorial.closest('.shopify-section') || editorial);
        }
        this.setupGlass();
        this.visibleVideos = new Set();
        this.userPausedVideos = new Set();
        const galleryVideos = [...this.querySelectorAll('[data-women-gallery-video]')].map(cell => ({ cell, player: cell.querySelector('video-media'), video: cell.querySelector('video') })).filter(item => item.video);
        const syncVideoControls = () => {
          galleryVideos.forEach(({ cell, player, video }) => {
            if (video.controls === mobileTabs.matches) video.controls = !mobileTabs.matches;
            if (mobileTabs.matches) {
              player.setAttribute('role', 'button');
              player.tabIndex = 0;
              player.setAttribute('aria-label', video.paused ? cell.dataset.playLabel : cell.dataset.pauseLabel);
            } else {
              player.removeAttribute('role');
              player.removeAttribute('tabindex');
              player.removeAttribute('aria-label');
            }
          });
        };
        this.videoControlsObserver = new MutationObserver(syncVideoControls);
        galleryVideos.forEach(({ player, video }) => {
          this.videoControlsObserver.observe(video, { attributes: true, attributeFilter: ['controls'] });
          video.addEventListener('play', syncVideoControls, { signal: this.controller.signal });
          video.addEventListener('pause', syncVideoControls, { signal: this.controller.signal });
          const togglePlayback = () => {
            if (!mobileTabs.matches) return;
            if (video.paused) { this.userPausedVideos.delete(player); player.play(); }
            else { this.userPausedVideos.add(player); player.pause(); }
          };
          player.addEventListener('click', togglePlayback, { signal: this.controller.signal });
          player.addEventListener('keydown', event => {
            if (!mobileTabs.matches || !['Enter', ' '].includes(event.key)) return;
            event.preventDefault();
            togglePlayback();
          }, { signal: this.controller.signal });
        });
        mobileTabs.addEventListener('change', syncVideoControls, { signal: this.controller.signal });
        syncVideoControls();
        this.videoObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            const player = entry.target.querySelector('video-media');
            if (entry.intersectionRatio >= .5) {
              this.visibleVideos.add(player);
              if (!document.hidden && !this.userPausedVideos.has(player)) player?.play();
            } else {
              this.visibleVideos.delete(player);
              player?.pause();
            }
          });
        }, { threshold: [0, .5] });
        this.querySelectorAll('[data-women-gallery-video]').forEach(cell => this.videoObserver.observe(cell));
        this.syncAvailability();
        this.setupSupportClearance(mobileTabs);
        const notify = this.querySelector('[data-nfd-back-in-stock]');
        if (notify) {
          this.notifyObserver = new IntersectionObserver(entries => {
            this.notifyVisible = entries[0].isIntersecting;
            this.syncStickyVisibility();
          });
          this.notifyObserver.observe(notify);
        }
        const sticky = this.querySelector('[data-women-sticky]');
        if (sticky) {
          this.stickyObserver = new IntersectionObserver(entries => {
            const entry = entries[0];
            this.passedBuycard = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
            this.syncStickyVisibility();
          });
          this.stickyObserver.observe(this.querySelector('.nfd-women-buycard'));
          const footer = document.querySelector('footer');
          if (footer) {
            this.footerObserver = new IntersectionObserver(entries => {
              this.footerVisible = entries[0].isIntersecting;
              this.syncStickyVisibility();
            });
            this.footerObserver.observe(footer);
          }
          this.buycardObserver = new MutationObserver(() => this.syncSticky());
          this.buycardObserver.observe(this.querySelector('.nfd-women-buycard'), { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-busy'], characterData: true });
          this.syncSticky();
        }
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.querySelectorAll('video-media').forEach(player => player.pause());
          else this.visibleVideos.forEach(player => { if (!this.userPausedVideos.has(player)) player?.play(); });
        }, { signal: this.controller.signal });
      });
    }
    setupGlass() {
      const card = this.querySelector('.nfd-women-buycard');
      const desktop = window.matchMedia('(min-width: 1000px)');
      const reduced = window.matchMedia('(prefers-reduced-transparency: reduce)');
      if (!card || !CSS.supports('backdrop-filter', 'blur(20px)') && !CSS.supports('-webkit-backdrop-filter', 'blur(20px)')) return;
      const cache = new WeakMap();
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return;
      const pixelsFor = image => {
        if (!image.complete || !image.naturalWidth) return null;
        const previous = cache.get(image);
        if (previous?.src === image.currentSrc) return previous.pixels;
        let pixels = null;
        try {
          canvas.width = canvas.height = 32;
          context.drawImage(image, 0, 0, 32, 32);
          pixels = context.getImageData(0, 0, 32, 32).data;
        } catch {}
        cache.set(image, { src: image.currentSrc, pixels });
        return pixels;
      };
      const update = () => {
        this.glassFrame = null;
        if (!desktop.matches || reduced.matches) {
          delete card.dataset.glassTone;
          return;
        }
        const cardRect = card.getBoundingClientRect();
        if (cardRect.bottom <= 0 || cardRect.top >= innerHeight) return;
        let brightness = 0;
        let count = 0;
        let uncertain = false;
        let mediaCount = 0;
        for (const across of [.2, .5, .8]) {
          for (const down of [.2, .5, .8]) {
            const x = cardRect.left + cardRect.width * across;
            const y = cardRect.top + cardRect.height * down;
            if (y < 0 || y >= innerHeight || x < 0 || x >= innerWidth) continue;
            const layers = document.elementsFromPoint(x, y);
            const image = layers.find(element => element instanceof HTMLImageElement && !card.contains(element) && element.closest('.nfd-women-gallery, .nfd-women-editorial-layout'));
            let value = 1;
            if (!image && layers.some(element => ['VIDEO', 'IFRAME'].includes(element.tagName) && element.closest('.nfd-women-gallery'))) { uncertain = true; mediaCount++; }
            if (image) {
              mediaCount++;
              const pixels = pixelsFor(image);
              if (pixels) {
                const rect = image.getBoundingClientRect();
                const fit = getComputedStyle(image).objectFit;
                const scale = fit === 'cover' ? Math.max(rect.width / image.naturalWidth, rect.height / image.naturalHeight) : Math.min(rect.width / image.naturalWidth, rect.height / image.naturalHeight);
                const width = fit === 'cover' || fit === 'contain' ? image.naturalWidth * scale : rect.width;
                const height = fit === 'cover' || fit === 'contain' ? image.naturalHeight * scale : rect.height;
                const nx = (x - rect.left - (rect.width - width) / 2) / width;
                const ny = (y - rect.top - (rect.height - height) / 2) / height;
                if (nx >= 0 && nx < 1 && ny >= 0 && ny < 1) {
                  const offset = (Math.floor(ny * 32) * 32 + Math.floor(nx * 32)) * 4;
                  value = (.2126 * pixels[offset] + .7152 * pixels[offset + 1] + .0722 * pixels[offset + 2]) / 255;
                }
              } else uncertain = true;
            }
            brightness += value;
            count++;
          }
        }
        const average = count ? brightness / count : 1;
        const tone = uncertain ? 'safe' : !mediaCount ? 'page' : average < .48 ? 'dark' : average > .78 ? 'bright' : 'mixed';
        if (card.dataset.glassTone !== tone) card.dataset.glassTone = tone;
      };
      const schedule = () => {
        if (!this.glassFrame) this.glassFrame = requestAnimationFrame(update);
      };
      document.addEventListener('scroll', schedule, { capture: true, passive: true, signal: this.controller.signal });
      document.addEventListener('load', schedule, { capture: true, signal: this.controller.signal });
      window.addEventListener('resize', schedule, { passive: true, signal: this.controller.signal });
      desktop.addEventListener('change', schedule, { signal: this.controller.signal });
      reduced.addEventListener('change', schedule, { signal: this.controller.signal });
      update();
    }
    syncAvailability() {
      if (!this.isConnected) return;
      const notify = this.querySelector('[data-nfd-back-in-stock]');
      if (!notify) return;
      notify.closest('[data-block-allow-rerender]')?.removeAttribute('data-block-allow-rerender');
      const variant = JSON.parse(this.querySelector('variant-picker script[data-variant]')?.textContent || '{}');
      notify.hidden = !variant.id || (variant.available !== false && notify.dataset.nfdComingSoon !== 'true');
      if (variant.id) notify.dataset.variantId = String(variant.id);
      const selected = this.getSizeOption();
      let context = notify.querySelector('[data-women-notify-context]');
      if (!context) {
        context = document.createElement('p');
        context.dataset.womenNotifyContext = '';
        context.className = 'nfd-women-notify-context';
        notify.prepend(context);
      }
      const message = selected && !notify.hidden
        ? (notify.dataset.nfdComingSoon === 'true' ? this.dataset.comingSize : this.dataset.notifySize).replace('__size__', selected.text)
        : '';
      if (context.textContent !== message) context.textContent = message;
      context.hidden = !message;
      this.syncRecommendationAvailability();
      if (selected?.input.id === this.pendingRecommendedNotify && !notify.hidden && !this.hasAttribute('aria-busy')) {
        this.pendingRecommendedNotify = null;
        notify.scrollIntoView({ behavior: 'smooth', block: 'center' });
        notify.querySelector('input[type="email"]')?.focus({ preventScroll: true });
      }
      this.syncSticky();
    }
    getSizeInputs() {
      const picker = this.querySelector('variant-picker') || this;
      const size = picker.querySelector('.variant-picker__option--size');
      return [...(size || picker).querySelectorAll(size ? 'input[data-option-position]' : 'input[data-option-position="1"]')];
    }
    getSizeOption(size) {
      const inputs = this.getSizeInputs();
      const options = inputs.map(input => {
        const label = this.querySelector('label[for="' + CSS.escape(input.id) + '"]');
        return { input, label, text: label?.textContent.trim() || '', unavailable: label?.classList.contains('is-disabled') };
      });
      return size === undefined ? options.find(option => option.input.checked) : options.find(option => option.text === size);
    }
    syncRecommendationAvailability() {
      const recommendation = this.querySelector('[data-rec-inline]');
      if (!recommendation) return;
      const size = recommendation.querySelector('.nfd-size-rec-result-title strong')?.textContent.trim();
      const option = size && this.getSizeOption(size);
      const notify = this.querySelector('[data-nfd-back-in-stock]');
      const unavailable = option?.unavailable || notify?.dataset.nfdComingSoon === 'true';
      let stock = recommendation.querySelector('[data-women-rec-stock]');
      if (!stock && option && unavailable) {
        stock = document.createElement('div');
        stock.dataset.womenRecStock = '';
        stock.className = 'nfd-women-rec-stock';
        stock.append(document.createElement('p'), document.createElement('button'));
        stock.lastElementChild.type = 'button';
        recommendation.append(stock);
      }
      if (stock) {
        stock.hidden = recommendation.hidden || !option || !unavailable || (option.input.checked && notify && !notify.hidden);
        if (!stock.hidden) {
          const message = (notify?.dataset.nfdComingSoon === 'true' ? this.dataset.recommendedComing : this.dataset.recommendedUnavailable).replace('__size__', size);
          if (stock.firstElementChild.textContent !== message) stock.firstElementChild.textContent = message;
          const action = stock.lastElementChild;
          action.hidden = !notify || !notify.hidden;
          action.dataset.womenRecommendedNotify = option.input.id;
          const label = this.dataset.notifySizeAction.replace('__size__', size);
          if (action.textContent !== label) action.textContent = label;
          action.disabled = this.hasAttribute('aria-busy');
        }
      }
    }
    setupSpecialChart() {
      const dialog = document.querySelector('[data-cats-pyjamas-dialog]');
      if (!dialog) return;
      dialog.dataset.womenInteractiveChart = '';
      const summary = document.createElement('p');
      summary.dataset.womenChartSelected = '';
      summary.className = 'nfd-women-chart-context';
      summary.setAttribute('aria-live', 'polite');
      dialog.querySelector('.cats-pyjamas-warning__dialog-header')?.append(summary);
      const sync = () => {
        if (!dialog.open) { this.specialChartWasOpen = false; return; }
        if (!this.specialChartWasOpen) this.chartHighlightedSize = null;
        this.specialChartWasOpen = true;
        this.syncChartSelection(false, dialog);
      };
      dialog.addEventListener('click', event => {
        const button = event.target.closest('[data-women-chart-size]');
        if (!button) return;
        event.preventDefault();
        this.chartHighlightedSize = button.dataset.womenChartSize;
        this.syncChartSelection(true, dialog);
      }, { signal: this.controller.signal });
      this.specialChartObserver = new MutationObserver(sync);
      this.specialChartObserver.observe(dialog, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['open', 'hidden'] });
      this.specialChartResizeObserver = new ResizeObserver(() => this.syncChartSelection(true, dialog));
      this.specialChartResizeObserver.observe(dialog);
      sync();
    }
    syncChartSelection(reveal = false, dialog = this.querySelector('[data-women-chart-dialog]')) {
      if (!dialog?.open) return;
      const selected = this.getSizeOption();
      const summary = dialog.querySelector('[data-women-chart-selected]');
      const highlighted = this.chartHighlightedSize ?? selected?.text;
      let matched = false;
      let scrollable = false;
      dialog.querySelectorAll('table.nfd-size-chart, table.cats-pyjamas-warning__table').forEach(table => {
        const headings = [...table.querySelectorAll('thead tr:first-child th')];
        headings.forEach((heading, i) => {
          if (!i || !heading.textContent.trim()) return;
          let button = heading.querySelector('[data-women-chart-size]');
          if (!button) {
            button = document.createElement('button');
            button.type = 'button';
            button.dataset.womenChartSize = heading.textContent.trim();
            button.textContent = heading.textContent.trim();
            button.setAttribute('aria-label', this.dataset.chartHighlight.replace('__size__', button.textContent));
            heading.replaceChildren(button);
          }
          button.setAttribute('aria-pressed', String(button.dataset.womenChartSize === highlighted));
        });
        const index = headings.findIndex((cell, i) => i > 0 && cell.textContent.trim() === highlighted);
        table.querySelectorAll('tr').forEach(row => [...row.cells].forEach((cell, i) => {
          cell.classList.toggle('is-selected-size', index > 0 && i === index);
        }));
        headings.forEach((heading, i) => {
          if (index > 0 && i === index) heading.setAttribute('aria-current', 'true');
          else heading.removeAttribute('aria-current');
        });
        const viewport = table.closest('.nfd-size-chart-scroll, .cats-pyjamas-warning__table-scroll');
        if (!viewport || !viewport.clientWidth) return;
        scrollable ||= viewport.scrollWidth > viewport.clientWidth + 1;
        if (index <= 0) return;
        matched = true;
        const key = highlighted + ':' + (table.closest('[data-sc-table]')?.dataset.scTable || table.closest('[data-cats-pyjamas-panel]')?.dataset.catsPyjamasPanel);
        if (reveal || viewport.dataset.womenRevealedSize !== key) {
          const cell = headings[index];
          const labelWidth = headings[0].getBoundingClientRect().width;
          const left = viewport.scrollLeft + cell.getBoundingClientRect().left - viewport.getBoundingClientRect().left;
          viewport.scrollLeft = Math.max(0, left - labelWidth - (viewport.clientWidth - labelWidth - cell.getBoundingClientRect().width) / 2);
          viewport.dataset.womenRevealedSize = key;
        }
      });
      if (summary) {
        const text = matched ? (this.chartHighlightedSize ? this.dataset.chartHighlighted : this.dataset.chartSelected).replace('__size__', highlighted) : '';
        if (summary.textContent !== text) summary.textContent = text;
        summary.hidden = !text;
      }
      const hint = dialog.querySelector('[data-women-chart-scroll-hint]');
      if (hint) hint.hidden = !scrollable;
    }
    formatPolicyContent() {
      this.querySelectorAll('[data-women-policy]').forEach(policy => {
        policy.querySelectorAll('p').forEach(paragraph => {
          const strong = paragraph.firstElementChild;
          if (strong?.tagName !== 'STRONG' || paragraph.children.length !== 1 || paragraph.textContent.trim() !== strong.textContent.trim()) return;
          const heading = document.createElement('h3');
          heading.append(...strong.childNodes);
          paragraph.replaceWith(heading);
        });
        policy.querySelectorAll('ul').forEach(list => {
          const items = [...list.children];
          if (items.length !== 1 || items[0].textContent.length < 180 || items[0].querySelector('a, button, input')) return;
          const content = items[0].textContent;
          const segments = typeof Intl.Segmenter === 'function'
            ? [...new Intl.Segmenter(document.documentElement.lang || 'en', { granularity: 'sentence' }).segment(content)].map(part => part.segment)
            : [content];
          const rules = document.createElement('div');
          rules.className = 'nfd-women-policy__rules';
          segments.forEach(segment => {
            const paragraph = document.createElement('p');
            paragraph.textContent = segment;
            rules.append(paragraph);
          });
          list.replaceWith(rules);
        });
      });
    }
    setupSupportClearance(mobile) {
      const launcher = document.getElementById('nfd-zendesk-launcher');
      if (!launcher) return;
      const update = () => {
        this.supportFrame = null;
        if (!mobile.matches || launcher.hidden) {
          launcher.style.removeProperty('--women-support-lift');
          return;
        }
        const rect = launcher.getBoundingClientRect();
        const currentLift = Number.parseFloat(launcher.style.getPropertyValue('--women-support-lift')) || 0;
        const baseTop = rect.top + currentLift;
        let top = baseTop;
        const obstacles = [...this.querySelectorAll('.buy-buttons, [data-nfd-back-in-stock] button, [data-women-sticky], [data-women-sticky-picker][open] .nfd-women-sticky__panel')]
          .map(element => element.getBoundingClientRect())
          .filter(obstacle => obstacle.width && obstacle.height && obstacle.right > rect.left && obstacle.left < rect.right)
          .sort((a, b) => b.bottom - a.bottom);
        obstacles.forEach(obstacle => {
          if (top < obstacle.bottom + 12 && top + rect.height > obstacle.top - 12) top = obstacle.top - 12 - rect.height;
        });
        launcher.style.setProperty('--women-support-lift', `${Math.max(0, baseTop - top)}px`);
      };
      this.scheduleSupportClearance = () => {
        if (!this.supportFrame) this.supportFrame = requestAnimationFrame(update);
      };
      document.addEventListener('scroll', this.scheduleSupportClearance, { capture: true, passive: true, signal: this.controller.signal });
      window.addEventListener('resize', this.scheduleSupportClearance, { passive: true, signal: this.controller.signal });
      mobile.addEventListener('change', this.scheduleSupportClearance, { signal: this.controller.signal });
      this.supportResizeObserver = new ResizeObserver(this.scheduleSupportClearance);
      this.supportResizeObserver.observe(launcher);
      this.supportResizeObserver.observe(this.querySelector('.nfd-women-buycard'));
      const sticky = this.querySelector('[data-women-sticky]');
      if (sticky) this.supportResizeObserver.observe(sticky);
      this.scheduleSupportClearance();
    }
    syncStickyVisibility() {
      const sticky = this.querySelector('[data-women-sticky]');
      if (sticky) sticky.hidden = !this.passedBuycard || this.footerVisible || (sticky.querySelector('[data-women-sticky-notify]') && this.notifyVisible);
      this.scheduleSupportClearance?.();
    }
    syncSticky() {
      const sticky = this.querySelector('[data-women-sticky]');
      if (!sticky) return;
      const selected = this.getSizeOption()?.input;
      const inputs = this.getSizeInputs();
      const options = sticky.querySelector('[data-women-sticky-options]');
      options.replaceChildren();
      inputs.forEach(input => {
        const label = this.querySelector('label[for="' + CSS.escape(input.id) + '"]');
        const text = label?.textContent.trim() || input.value;
        const unavailable = label?.classList.contains('is-disabled');
        const button = document.createElement('button');
        button.type = 'button'; button.dataset.womenStickyOption = input.id;
        button.className = 'nfd-women-sticky__option';
        button.classList.toggle('is-unavailable', unavailable);
        button.setAttribute('aria-pressed', String(input.checked));
        button.disabled = this.hasAttribute('aria-busy');
        const name = document.createElement('span'); name.textContent = text;
        const state = document.createElement('span');
        state.textContent = unavailable ? this.dataset.notifyLabel : input.checked ? '✓' : '';
        button.append(name, state); options.append(button);
      });
      const selectedLabel = selected && this.querySelector('label[for="' + CSS.escape(selected.id) + '"]');
      sticky.querySelector('[data-women-sticky-selected]').textContent = selectedLabel?.textContent.trim() || '';
      sticky.querySelector('.nfd-women-sticky__size').hidden = inputs.length < 2;
      const source = this.querySelector('.buy-buttons button[type="submit"]');
      const submit = sticky.querySelector('[data-women-sticky-submit]');
      const notify = this.querySelector('[data-nfd-back-in-stock]');
      const canNotify = notify && !notify.hidden;
      submit.toggleAttribute('data-women-sticky-notify', canNotify);
      submit.type = canNotify ? 'button' : 'submit';
      submit.textContent = canNotify ? this.dataset.notifyLabel : source?.textContent.trim() || this.dataset.selectSize;
      submit.disabled = this.hasAttribute('aria-busy') || (!canNotify && (!source || source.disabled));
      const price = this.querySelector('.nfd-women-buycard .price-list')?.cloneNode(true);
      if (price) {
        price.removeAttribute('id');
        price.querySelectorAll('[id]').forEach(item => item.removeAttribute('id'));
        sticky.querySelector('[data-women-sticky-price]').replaceChildren(price);
      }
      sticky.querySelector('[data-women-sticky-fabric]').textContent = this.querySelector('.nfd-women-heading__fabric')?.textContent.trim() || '';
    }
    async selectSize(input) {
      const picker = this.querySelector('variant-picker');
      const form = document.forms[picker.getAttribute('form-id')];
      const previousVariant = JSON.parse(picker.querySelector('script[data-variant]')?.textContent || '{}');
      const optionValues = Array.from(form.elements)
        .filter(element => element.matches('input[data-option-position]:checked'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map(element => element.value);
      const url = new URL(window.location.href);
      if (input.dataset.productUrl) {
        const destination = new URL(input.dataset.productUrl, window.location.origin);
        if (url.searchParams.has('view')) destination.searchParams.set('view', url.searchParams.get('view'));
        const preview = url.searchParams.get('preview_theme_id');
        if (preview) destination.searchParams.set('preview_theme_id', preview);
        window.location.assign(destination.href);
        return;
      }
      url.searchParams.delete('variant');
      const sizeVariants = JSON.parse(this.querySelector('[data-women-size-variants]')?.textContent || '{}');
      const variantId = sizeVariants[input.value];
      if (variantId) {
        url.searchParams.delete('option_values');
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.set('option_values', optionValues.join(','));
      }
      url.searchParams.set('section_id', this.dataset.sectionId);
      const sequence = this.sequence = (this.sequence || 0) + 1;
      this.setAttribute('aria-busy', 'true');
      this.syncSticky();
      this.querySelector('[data-women-variant-error]')?.remove();
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Variant request failed');
        const fragment = document.createRange().createContextualFragment(await response.text());
        if (sequence !== this.sequence) return;
        const nextPicker = fragment.querySelector('variant-picker');
        if (!nextPicker) throw new Error('Variant picker missing');
        const variant = JSON.parse(nextPicker.querySelector('script[data-variant]')?.textContent || '{}');
        const idInput = form.querySelector('input[name="id"]');
        idInput.value = variant?.id || '';
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
        form.dispatchEvent(new CustomEvent('product:rerender', { detail: { htmlFragment: fragment, productChange: false } }));
        form.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { formId: form.getAttribute('id'), variant, previousVariant } }));
        if (variant?.id) {
          const location = new URL(window.location.href);
          location.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', location);
        }
        window.Shopify?.PaymentButton?.init();
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.sequence) return;
        const message = document.createElement('p');
        message.dataset.womenVariantError = '';
        message.setAttribute('role', 'alert');
        message.textContent = this.dataset.variantError;
        picker.after(message);
      } finally {
        if (sequence === this.sequence) this.removeAttribute('aria-busy');
        this.syncAvailability();
        this.syncSticky();
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.introResizeObserver?.disconnect();
      this.editorialResizeObserver?.disconnect();
      cancelAnimationFrame(this.detailsFrame);
      cancelAnimationFrame(this.glassFrame);
      document.querySelector('[data-women-editorial-product="' + this.dataset.productId + '"] [data-women-floating-details]')?.removeAttribute('data-women-floating-details');
      this.videoObserver?.disconnect();
      this.videoControlsObserver?.disconnect();
      this.stickyObserver?.disconnect();
      this.footerObserver?.disconnect();
      this.buycardObserver?.disconnect();
      this.recommendationObserver?.disconnect();
      this.sizeToolsObserver?.disconnect();
      this.chartObserver?.disconnect();
      this.specialChartObserver?.disconnect();
      this.specialChartResizeObserver?.disconnect();
      this.notifyObserver?.disconnect();
      this.chartResizeObserver?.disconnect();
      this.supportResizeObserver?.disconnect();
      cancelAnimationFrame(this.supportFrame);
      document.getElementById('nfd-zendesk-launcher')?.style.removeProperty('--women-support-lift');
      this.querySelectorAll('video-media').forEach(player => player.pause());
    }
  });
}
